import { Feature, Map, Overlay } from "ol";
import Polygon from 'ol/geom/Polygon';
import VectorLayer from "ol/layer/Vector";
import { Stroke, Fill, Style } from "ol/style";
import VectorSource from "ol/source/Vector";
import { TooltipManager } from "./tooltip";
import { geojson } from "@lib/geojson";
import { FeatureTooltip } from "./marker";
import Modify, { ModifyEvent } from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';

export interface PolygonType {
    id?: number,
    coordinates: geojson.Coord[][],
    content: string,
    color: string,
}

export class PolygonManager {
    private readonly mapElement: Map;
    private readonly tooltip: TooltipManager;
    public polygonFeatures = new Set<FeatureTooltip<Polygon>>();
    private vectorSource = new VectorSource();
    private vectorLayer: VectorLayer;
    private modifyInteraction: Modify | null = null;
    private snapInteraction: Snap | null = null;

    constructor(mapElement: Map, tooltip: TooltipManager) {
        this.mapElement = mapElement;
        this.tooltip = tooltip;
        this.vectorLayer = new VectorLayer({ source: this.vectorSource });
        this.mapElement.addLayer(this.vectorLayer);
    }

    add(item: PolygonType) {
        const polygonFeature = new Feature(new Polygon(item.coordinates));
        polygonFeature.set('name', item.content);
        polygonFeature.set('polygonId', item.id);
        
        this.renderPolygon(polygonFeature, item.color);
        this.addHoverEvents(polygonFeature, item);
        this.polygonFeatures.add({
            feature: polygonFeature,
            content: item.content,
            coordinate: item.coordinates[0][0],
            category: 'group',
            type: 'muckpile'
        });
    }

    public clearAll() {
        this.stopEditing();
        this.vectorSource.clear();
        this.tooltip.hideAllTooltips('muckpile');
        this.polygonFeatures.clear();
    }

    public startEditing(
        onChange: (id: number, coordinates: geojson.Coord[][]) => Promise<boolean>
    ): boolean {
        this.stopEditing();

        if (this.vectorSource.getFeatures().length === 0) {
            return false;
        }

        this.modifyInteraction = new Modify({ source: this.vectorSource });
        this.snapInteraction = new Snap({ source: this.vectorSource });

        this.modifyInteraction.on('modifystart', (event: ModifyEvent) => {
            event.features.forEach(feature => {
                const geometry = feature.getGeometry();
                if (geometry instanceof Polygon) {
                    const coordinates = geometry.getCoordinates().map(ring =>
                        ring.map(coordinate => [...coordinate])
                    );
                    feature.set('coordinatesBeforeEdit', coordinates);
                }
            });
        });

        this.modifyInteraction.on('modifyend', (event: ModifyEvent) => {
            void this.handleModifyEnd(event, onChange);
        });

        this.mapElement.addInteraction(this.modifyInteraction);
        this.mapElement.addInteraction(this.snapInteraction);
        return true;
    }

    public stopEditing(): void {
        if (this.modifyInteraction) {
            this.mapElement.removeInteraction(this.modifyInteraction);
            this.modifyInteraction = null;
        }
        if (this.snapInteraction) {
            this.mapElement.removeInteraction(this.snapInteraction);
            this.snapInteraction = null;
        }
    }

    private async handleModifyEnd(
        event: ModifyEvent,
        onChange: (id: number, coordinates: geojson.Coord[][]) => Promise<boolean>
    ): Promise<void> {
        const feature = event.features.item(0);
        const geometry = feature?.getGeometry();
        const id = feature?.get('polygonId');
        const coordinatesBeforeEdit = feature?.get('coordinatesBeforeEdit') as geojson.Coord[][];
        const activeModifyInteraction = this.modifyInteraction;

        if (!(geometry instanceof Polygon) || typeof id !== 'number') {
            this.restoreCoordinates(feature, coordinatesBeforeEdit);
            return;
        }

        activeModifyInteraction?.setActive(false);
        try {
            const saved = await onChange(id, geometry.getCoordinates() as geojson.Coord[][]);
            if (!saved) {
                this.restoreCoordinates(feature, coordinatesBeforeEdit);
            }
        } finally {
            if (this.modifyInteraction === activeModifyInteraction) {
                activeModifyInteraction?.setActive(true);
            }
        }
    }

    private restoreCoordinates(feature: Feature | undefined, coordinates: geojson.Coord[][]): void {
        const geometry = feature?.getGeometry();
        if (geometry instanceof Polygon && coordinates) {
            geometry.setCoordinates(coordinates);
        }
    }

    private renderPolygon(polygonFeature: Feature<Polygon>, color: string) {
        let style = new Style({
            fill: new Fill({ color: hexToRGBA(color, 0.7) }),
            stroke: new Stroke({ color: hexToRGBA(color, 1), width: 1.5 }),
        });

        polygonFeature.setStyle(style);
        this.vectorSource.addFeature(polygonFeature);
    }

    private addHoverEvents(polygonFeature: Feature<Polygon>, item: PolygonType) {
        this.mapElement.on('pointermove', (event) => {
            const pixel = this.mapElement.getEventPixel(event.originalEvent);
            const featureAtPixel = this.mapElement.forEachFeatureAtPixel(pixel, (feature) => feature);

            if (featureAtPixel === polygonFeature) {
                this.tooltip.showTooltip([{ feature: polygonFeature, content: item.content, coordinate: item.coordinates[0][0], category: 'individual', type: 'muckpile' }]);
            } else {
                this.tooltip.hideTooltip(polygonFeature);
            }
        });
    }
}

function hexToRGBA(hex: string, opacity: number): string {
    const trimmedHex = hex.replace('#', '');
    const r = parseInt(trimmedHex.substring(0, 2), 16);
    const g = parseInt(trimmedHex.substring(2, 4), 16);
    const b = parseInt(trimmedHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}